import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { locationPoiData } from '@/mocks/fizboData';

interface HaritaLokasyonProps {
  location: string;
}

export default function HaritaLokasyon({ location }: HaritaLokasyonProps) {
  const { isDark } = useTheme();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const locationScores = locationPoiData.map((cat) => ({
    category: cat.category,
    avg: Math.round(cat.items.reduce((s, i) => s + i.score, 0) / cat.items.length),
  }));
  const overallScore = Math.round(locationScores.reduce((s, c) => s + c.avg, 0) / locationScores.length);

  const categoryIcons: Record<string, string> = {
    Eğitim: 'ri-school-line',
    Sağlık: 'ri-hospital-line',
    Ulaşım: 'ri-bus-line',
    Alışveriş: 'ri-store-2-line',
  };

  const categoryColors: Record<string, string> = {
    Eğitim: 'text-sky-500',
    Sağlık: 'text-red-500',
    Ulaşım: 'text-green-500',
    Alışveriş: 'text-amber-500',
  };

  const categoryBg: Record<string, string> = {
    Eğitim: 'bg-sky-50 dark:bg-sky-900/20',
    Sağlık: 'bg-red-50 dark:bg-red-900/20',
    Ulaşım: 'bg-green-50 dark:bg-green-900/20',
    Alışveriş: 'bg-amber-50 dark:bg-amber-900/20',
  };

  return (
    <div className="space-y-4">
      {/* Overall Score */}
      <div className={`rounded-xl p-4 flex items-center gap-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 ${
          overallScore >= 85 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'
        }`}>
          <span className={`text-2xl font-bold ${overallScore >= 85 ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
            {overallScore}
          </span>
          <span className={`text-xs ${overallScore >= 85 ? 'text-green-500' : 'text-yellow-500'}`}>puan</span>
        </div>
        <div>
          <p className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Lokasyon Avantaj Skoru
          </p>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            {location}
          </p>
          <p className={`text-xs mt-1 font-medium ${overallScore >= 85 ? 'text-green-500' : 'text-yellow-500'}`}>
            {overallScore >= 85 ? 'Mükemmel Lokasyon' : overallScore >= 70 ? 'İyi Lokasyon' : 'Orta Lokasyon'}
          </p>
        </div>
      </div>

      {/* Category Scores */}
      <div className="grid grid-cols-2 gap-2">
        {locationScores.map((cat) => (
          <button
            key={cat.category}
            onClick={() => setActiveCategory(activeCategory === cat.category ? null : cat.category)}
            className={`rounded-xl p-3 text-left transition-all cursor-pointer border-2 ${
              activeCategory === cat.category
                ? 'border-primary-500'
                : isDark ? 'border-dark-border' : 'border-light-border'
            } ${categoryBg[cat.category]}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${categoryIcons[cat.category]} text-sm ${categoryColors[cat.category]}`} />
              </div>
              <span className={`text-xs font-bold ${categoryColors[cat.category]}`}>{cat.avg}</span>
            </div>
            <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {cat.category}
            </p>
            <div className={`mt-1.5 w-full h-1 rounded-full ${isDark ? 'bg-dark-border' : 'bg-white/60'}`}>
              <div
                className={`h-full rounded-full ${cat.avg >= 85 ? 'bg-green-500' : cat.avg >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${cat.avg}%` }}
              />
            </div>
          </button>
        ))}
      </div>

      {/* POI Detail */}
      {activeCategory && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className={`px-4 py-2.5 flex items-center gap-2 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${categoryIcons[activeCategory]} text-xs ${categoryColors[activeCategory]}`} />
            </div>
            <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {activeCategory} Detayları
            </p>
          </div>
          {locationPoiData.find(c => c.category === activeCategory)?.items.map((item, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between px-4 py-3 ${
                idx > 0 ? (isDark ? 'border-t border-dark-border' : 'border-t border-light-border') : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-xs ${categoryColors[activeCategory]}`} />
                </div>
                <div>
                  <p className={`text-xs font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{item.name}</p>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.distance}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                  <div
                    className={`h-full rounded-full ${item.score >= 85 ? 'bg-green-500' : 'bg-yellow-500'}`}
                    style={{ width: `${item.score}%` }}
                  />
                </div>
                <span className={`text-xs font-bold w-6 text-right ${item.score >= 85 ? 'text-green-500' : 'text-yellow-500'}`}>
                  {item.score}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Google Maps Embed */}
      <div>
        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Harita Görünümü
        </p>
        <div className="w-full h-48 rounded-xl overflow-hidden border border-light-border dark:border-dark-border">
          <iframe
            title="Konum Haritası"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodeURIComponent(location + ', Samsun, Türkiye')}&zoom=15`}
          />
        </div>
        <p className={`text-xs mt-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Yakın çevre: okul, hastane, ulaşım noktaları işaretli
        </p>
      </div>
    </div>
  );
}
